import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "@/lib/utils";

/**
 * A tooltip in ADVisor explains the CONSEQUENCE of an action. It never repeats
 * the button's name.
 *
 *   ✅ "Camila puede publicar — el pago sigue retenido"
 *   ❌ "Aprobar el entregable"
 *
 * This is a cross-cutting standard, not a nicety: every approve / reject /
 * publish / dismiss button in the product carries one. When money moves or a
 * turn changes hands, the person clicking is entitled to know what happens next
 * before they commit.
 */

const TooltipProvider = TooltipPrimitive.Provider;
const TooltipRoot = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 8, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    // Radix flips and shifts the tip away from the viewport edge on its own, so
    // a button flush with the right edge no longer needs its tooltip anchored
    // by hand — the hand-rolled version in the prototypes did, and clipped when
    // whoever added the button forgot.
    collisionPadding={12}
    className={cn(
      "z-50 whitespace-nowrap rounded-tooltip bg-neutral-800 px-2.5 py-1.5",
      "text-micro font-medium text-white shadow-tooltip",
      "pointer-events-none select-none",
      "animate-in fade-in-0 duration-150",
      className,
    )}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export interface TooltipProps
  extends Pick<TooltipPrimitive.TooltipContentProps, "side" | "align"> {
  /** The consequence, in one line. Not the button's label. */
  label: React.ReactNode;
  /** Skip rendering the tip without unmounting the child — for disabled states. */
  disabled?: boolean;
  delayDuration?: number;
  children: React.ReactNode;
}

/**
 * The everyday form. Wraps any trigger:
 *
 *   <Tooltip label="Camila puede publicar — el pago sigue retenido">
 *     <Button>Aprobar</Button>
 *   </Tooltip>
 *
 * The Radix parts are exported below for the rare case that needs them.
 */
function Tooltip({
  label,
  disabled = false,
  side = "top",
  align = "center",
  delayDuration = 120,
  children,
}: TooltipProps) {
  if (disabled || !label) return <>{children}</>;

  return (
    <TooltipProvider delayDuration={delayDuration} skipDelayDuration={300}>
      <TooltipRoot>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipPrimitive.Portal>
          <TooltipContent side={side} align={align}>
            {label}
          </TooltipContent>
        </TooltipPrimitive.Portal>
      </TooltipRoot>
    </TooltipProvider>
  );
}

export {
  Tooltip,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  TooltipContent,
};
